import { Flex, Paper, Text } from '@mantine/core';
import { useMemo } from 'react';
import { SiestaStepCounter } from 'src/UI/generate-flow/SiestaStepCounter';
import { SiestaStepCard } from 'src/UI/generate-flow/SiestaStepCard';
import {
  ProcessingSidebarAnimation,
  SummaryItem,
} from 'src/UI/generate-flow/ProcessingSidebarAnimation';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { AgendaData, AgendaStepId } from '../types';

type StepDef = {
  id: AgendaStepId;
  title: string;
};

type Props = {
  stepIndex: number;
  steps: StepDef[];
  data: AgendaData;
  currentStepId: AgendaStepId;
  isSubmitting?: boolean;
};

export function AgendaSidebar({
  stepIndex,
  steps,
  data,
  currentStepId,
  isSubmitting = false,
}: Props) {
  let clientName = '';
  if (data.isFamily) {
    if (data.family) clientName = `${data.family.name} Family`;
  } else if (data.client) {
    clientName = `${data.client.firstName} ${data.client.lastName}`;
  }
  const clientId = data.isFamily ? data.family?.mainFamilyMember.clientId : data.client?.id;
  const navClientId = data.isFamily ? data.fileOwnerClientId : data.client?.id;

  const hasSelection = useMemo(() => {
    switch (currentStepId) {
      case 'client':
        return !!clientId;
      case 'template':
        return !!data.template;
      default:
        return false;
    }
  }, [currentStepId, clientId, data.template]);

  if (currentStepId === 'processing') {
    const items: SummaryItem[] = [
      ...(clientName.trim()
        ? [{ id: 'client', label: data.isFamily ? 'Family' : 'Client', value: clientName }]
        : []),
      ...(data.template
        ? [{ id: 'template', label: 'Template', value: data.template.displayName }]
        : []),
    ];

    return (
      <ProcessingSidebarAnimation
        items={items}
        clientFirstName={data.isFamily ? data.family?.name : data.client?.firstName}
        documentLabel="Meeting agenda"
        isSubmitting={isSubmitting}
        navigateTo={`/client/${navClientId}/document/meeting-agendas`}
      />
    );
  }

  return (
    <Flex
      direction="column"
      h="100%"
      pos="relative"
      style={{ transition: 'all 300ms ease-in-out' }}
    >
      {stepIndex >= 1 && (
        <Flex justify="center" style={{ flexShrink: 0 }}>
          <Paper
            radius="xl"
            p="xxs"
            bg="rgba(0, 0, 0, 0.2)"
            w={374}
            style={{
              backdropFilter: 'blur(48px)',
              border: '1px solid rgba(253,253,253,0.15)',
            }}
          >
            <Flex align="center" justify="space-between" px="md" pt="xs">
              <Text size="xs" fw={FONT_WEIGHTS.medium} c="white">
                {data.isFamily ? 'Family' : 'Client'}
              </Text>
            </Flex>
            <Text size="md" fw={FONT_WEIGHTS.bold} c="white" px="md" pb="xs" truncate>
              {clientName}
            </Text>
          </Paper>
        </Flex>
      )}

      <Flex direction="column" align="center" justify="center" flex={1} mih={0}>
        <Flex
          direction="column"
          align="center"
          gap="xs"
          style={{
            marginTop: stepIndex >= 1 ? '-32px' : 0,
            transition: 'all 300ms ease-in-out',
          }}
        >
          <SiestaStepCounter currentStep={stepIndex + 1} totalSteps={steps.length - 1} />
          <SiestaStepCard
            steps={steps}
            currentStep={stepIndex + 1}
            dimmed={false}
            minimized={hasSelection}
          />
        </Flex>
      </Flex>
    </Flex>
  );
}
